import React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Rocket, AlertCircle, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import type { AdRow } from "@/types/bulkAd";
import type { LaunchResult } from "@/hooks/useAdSheetLogic";

interface ReviewAdsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: AdRow[];
  onLaunch: () => Promise<LaunchResult>;
  isLaunching?: boolean;
  launchProgress?: { current: number; total: number } | null;
  processingRowIds?: Set<string>;
}

export function ReviewAdsModal({
  open,
  onOpenChange,
  rows,
  onLaunch,
  isLaunching = false,
  launchProgress = null,
  processingRowIds = new Set(),
}: ReviewAdsModalProps) {
const [launchResult, setLaunchResult] = useState<LaunchResult | null>(null);
const [hasLaunched, setHasLaunched] = useState(false);
const launchTotalRef = useRef(0);

  // Filter rows that have required fields filled and are in Draft stage
  const validRows = rows.filter(
    (row) => row.adName && row.campaign && row.adSet && row.stage === "Draft"
  );
  const invalidRows = rows.filter(
    (row) => !row.adName || !row.campaign || !row.adSet
  );

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setLaunchResult(null);
      setHasLaunched(false);
      launchTotalRef.current = 0;
    }
  }, [open]);

  // Snapshot total when launching begins
  useEffect(() => {
    if (isLaunching && launchTotalRef.current === 0) {
      launchTotalRef.current = validRows.length;
    }
  }, [isLaunching, validRows.length]);

  // Group by campaign for summary
  const campaignGroups = validRows.reduce((acc, row) => {
    const campaignName = row.campaign?.name || "No Campaign";
    if (!acc[campaignName]) {
      acc[campaignName] = [];
    }
    acc[campaignName].push(row);
    return acc;
  }, {} as Record<string, AdRow[]>);

const handleLaunch = async () => {
    console.log('[ReviewAdsModal.handleLaunch] Launch button clicked');
    console.log('[ReviewAdsModal.handleLaunch] Valid rows to launch:', validRows.length);
    setHasLaunched(true);
    console.log('[ReviewAdsModal.handleLaunch] Calling onLaunch()...');
    const result = await onLaunch();
    console.log('[ReviewAdsModal.handleLaunch] onLaunch result:', result);
    setLaunchResult(result);
  };

  const progressPercentage = launchProgress
    ? (launchProgress.current / launchProgress.total) * 100
    : 0;

  // Show completion state
  const isComplete = hasLaunched && !isLaunching && launchResult !== null;

  return (
    <Dialog open={open} onOpenChange={isLaunching ? undefined : onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isComplete ? (
              launchResult?.success ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-600" />
              )
            ) : (
              <Rocket className="h-5 w-5" />
            )}
            {isComplete
              ? launchResult?.success
                ? "Launch Complete"
                : "Launch Completed with Errors"
              : "Review & Launch Ads"}
          </DialogTitle>
           <DialogDescription>
             {isComplete
               ? `${launchResult?.launched || 0} ads launched successfully${
                   launchResult?.failed ? `, ${launchResult.failed} failed` : ""
                 }`
                 : isLaunching
                 ? launchProgress
                   ? `${launchTotalRef.current - launchProgress.current + 1} of ${launchTotalRef.current} remaining...`
                   : `Preparing launch...`
               : "Review your ads before launching them to Meta."}
           </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Progress bar during launch */}
{isLaunching && launchProgress && (
            <div className="space-y-2">
              <Progress value={progressPercentage} className="h-2" />
            </div>
          )}

          {/* Completion summary */}
          {isComplete && launchResult && (
            <div className="space-y-3">
              <div className="flex items-center justify-around p-4 bg-muted rounded-lg">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="text-2xl font-bold">{launchResult.launched}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Launched</p>
                </div>
                {launchResult.failed > 0 && (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-red-600">
                      <XCircle className="h-5 w-5" />
                      <span className="text-2xl font-bold">{launchResult.failed}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Failed</p>
                  </div>
                )}
              </div>

              {/* Error details */}
              {launchResult.errors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600">Failed Ads:</p>
                  <ScrollArea className="max-h-32">
                    <div className="space-y-2">
                      {launchResult.errors.map((err, idx) => (
                        <div
                          key={idx}
                          className="p-2 bg-red-50 border border-red-200 rounded text-sm"
                        >
                          <p className="font-medium text-red-800">{err.adName || "Unknown ad"}</p>
                          <p className="text-red-600 text-xs">{err.error}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}

          {/* Pre-launch view */}
          {!isComplete && !isLaunching && (
            <>
              {/* Summary stats */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="text-sm">
                  <span className="font-medium">{validRows.length}</span> ads ready to launch
                </div>
                {invalidRows.length > 0 && (
                  <div className="flex items-center gap-1 text-sm text-amber-600">
                    <AlertCircle className="h-4 w-4" />
                    <span>{invalidRows.length} incomplete</span>
                  </div>
                )}
              </div>

              {/* Campaign breakdown */}
              {Object.keys(campaignGroups).length > 0 ? (
                <ScrollArea className="max-h-64">
                  <div className="space-y-3">
                    {Object.entries(campaignGroups).map(([campaignName, ads]) => (
                      <div key={campaignName} className="border rounded-lg p-3">
                        <div className="font-medium text-sm mb-2">{campaignName}</div>
                        <div className="space-y-1">
                          {ads.map((ad) => (
                            <div
                              key={ad.id}
                              className="flex items-center justify-between text-sm text-muted-foreground"
                            >
                              <span className="truncate flex-1">{ad.adName}</span>
                              <span className="text-xs bg-secondary px-2 py-0.5 rounded ml-2">
                                {ad.adSet?.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No valid ads to launch.</p>
                  <p className="text-sm">
                    Ensure each ad has a name, campaign, and ad set selected.
                  </p>
                </div>
              )}

              {/* Incomplete ads warning */}
              {invalidRows.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <strong>Note:</strong> {invalidRows.length} ad
                  {invalidRows.length > 1 ? "s" : ""} will be skipped due to missing
                  required fields (name, campaign, or ad set).
                </div>
              )}
            </>
          )}

          {/* In-progress view - show which rows are being processed */}
          {isLaunching && !isComplete && (
            <ScrollArea className="max-h-48">
              <div className="space-y-2">
                {validRows.map((ad) => {
                  const isProcessing = processingRowIds.has(ad.id);
                  const isLaunched = ad.stage === "Launched";

                  return (
                    <div
                      key={ad.id}
                      className={`flex items-center justify-between p-2 rounded text-sm ${
                        isProcessing
                          ? "bg-blue-50 border border-blue-200"
                          : isLaunched
                          ? "bg-green-50 border border-green-200"
                          : "bg-muted"
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isProcessing ? (
                          <Loader2 className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />
                        ) : isLaunched ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                        )}
                        <span className="truncate">{ad.adName}</span>
                      </div>
                      <span className="text-xs text-muted-foreground ml-2">
                        {ad.campaign?.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          {isComplete ? (
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLaunching}
              >
                Cancel
              </Button>
              <Button
                onClick={handleLaunch}
                disabled={validRows.length === 0 || isLaunching}
              >
                {isLaunching ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Launching...
                  </>
                ) : (
                  `Launch ${validRows.length} Ad${validRows.length !== 1 ? "s" : ""}`
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
